import * as couponService from '@cart-services/core/couponService'
import { type GiveawayValue, type MergedCoupon, type SelectedCouponGiveawayProductMap } from '@cart-services/types'
import { CART_FORM_ERROR_MESSAGE, isAllGiveawayValuesUnavailable, isGiveawayValuePartiallyUnavailable } from '@cart-services/zod-schema/cartFormSchema'
import { ProjectCode } from '@constants'
import { sumOfArray } from '@utils'
import { z } from 'zod'
import { type CartFormValues } from '../components/CartProvider/CartFormProvider'

export interface CartFormContext {
  regularGiveaways?: MergedCoupon[]
  selectedCouponGiveawayMap: Partial<Record<number, SelectedCouponGiveawayProductMap>>
  isGiveawayDispatch: boolean
  giveaways?: GiveawayValue[]
}

export const zodCampaignCartSchema = (context: CartFormContext): z.ZodType<CartFormValues> => {
  return z
    .object({
      regularGiveaways: z.array(z.string().optional()),
      giveaways: z.array(z.object({
        couponCriterionId: z.number(),
        accumulatedGiftSelectionCount: z.number(),
        totalSelected: z.number(),
        criterionCount: z.number().optional(),
        isGiveAll: z.boolean(),
        giveawayValuesWithValidateStock: z.array(z.object({
          product_id: z.string(),
          sku: z.string(),
          quantity: z.number(),
          stock: z.number().optional(),
          project_code: z.nativeEnum(ProjectCode),
        })),
      })).optional(),
    })
    .superRefine(({ regularGiveaways }, ctx) => {
      if (!context.isGiveawayDispatch) return
      const regularGiveawaysList = context.regularGiveaways?.flatMap(regularGiveaway => regularGiveaway.criterions)
      regularGiveawaysList?.forEach((criterion, index) => {
        const {
          isAllUnavailable,
          isApplied,
        } = couponService.getRegularGiveawaySelectProps(criterion, context.selectedCouponGiveawayMap)
        const isRequired = isApplied && !isAllUnavailable
        if (isRequired && !regularGiveaways[index]) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: '請選擇滿額禮！',
            path: ['regularGiveaways', index],
          })
        }
      })
    })
    .superRefine(({ giveaways }, ctx) => {
      giveaways?.forEach((giveaway, index) => {
        const needSelectGiveaway = giveaway.accumulatedGiftSelectionCount > 0
        const totalStock = sumOfArray(giveaway.giveawayValuesWithValidateStock, (acc, giveawayValue) => acc + (giveawayValue.stock || 0))
        const isPartiallyNotEnough = totalStock < giveaway.accumulatedGiftSelectionCount
        const isAllUnavailable = isAllGiveawayValuesUnavailable(giveaway.giveawayValuesWithValidateStock)
        const hasSelectGiveaway = giveaway.totalSelected > 0
        const hasSelectAll = isPartiallyNotEnough
          ? giveaway.totalSelected === totalStock
          : giveaway.accumulatedGiftSelectionCount === giveaway.totalSelected

        /**
         * 贈品部分不足
         * ex: 所選的贈品結帳前發現庫存不足，但還有其他可選的
         */
        if (needSelectGiveaway && !isAllUnavailable) {
          giveaway.giveawayValuesWithValidateStock.forEach((giveawayValue) => {
            if (isGiveawayValuePartiallyUnavailable(giveawayValue)) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: CART_FORM_ERROR_MESSAGE.NOT_ENOUGH_GIVEAWAY_PARTIALLY,
                path: ['giveaways', index],
              })
            }
          })
        }

        /**
         * 贈品全部不足
         * ex: 所選的贈品結帳前發現全部庫存都不足
         */
        if (isAllUnavailable && hasSelectGiveaway) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: CART_FORM_ERROR_MESSAGE.NOT_ENOUGH_GIVEAWAY,
            path: ['giveaways', index],
          })
        }

        /**
         * 未選擇贈品
         */
        if (needSelectGiveaway && !isAllUnavailable && (!hasSelectGiveaway || !hasSelectAll)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: CART_FORM_ERROR_MESSAGE.NOT_ENOUGH_GIVEAWAY_SELECTED,
            path: ['giveaways', index],
          })
        }
      })
    })
}
